import axios from 'axios';
import {
  Listing,
  ListingsResponse,
  AdResponse,
  ClaimRequest,
  Lead,
  AssistantResponse,
  FirstPartyReviewsResponse,
} from '../types';

const adserverBase = process.env.REACT_APP_ADSERVER_URL || 'http://localhost:3000';
const phpApiBase   = process.env.REACT_APP_PHP_API_URL || 'https://admin.topratedrehabs.com';

const api    = axios.create({ baseURL: adserverBase });
const phpApi = axios.create({ baseURL: phpApiBase });

export const submitPublicReview = async (payload: {
  listingId: number;
  authorEmail: string;
  author?: string;
  text: string;
  ratingTreatmentEffectiveness?: number;
  ratingAccommodationsAmenities?: number;
  ratingMealsNutrition?: number;
  recaptchaToken?: string;
}) => {
  const params = new URLSearchParams();
  params.set('listing_id', String(payload.listingId));
  params.set('author_email', payload.authorEmail);
  if (payload.author) params.set('author', payload.author);
  params.set('text', payload.text);
  if (payload.ratingTreatmentEffectiveness != null)
    params.set('rating_treatment_effectiveness', String(payload.ratingTreatmentEffectiveness));
  if (payload.ratingAccommodationsAmenities != null)
    params.set('rating_accommodations_amenities', String(payload.ratingAccommodationsAmenities));
  if (payload.ratingMealsNutrition != null)
    params.set('rating_meals_nutrition', String(payload.ratingMealsNutrition));
  if (payload.recaptchaToken) params.set('recaptcha_token', payload.recaptchaToken);
  const { data } = await phpApi.post('/api/public/review/start-verify', params.toString(), {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
  return data;
};

export const submitEditorialInquiry = async (payload: {
  listingId: number;
  inquirerEmail: string;
  inquirerName?: string;
  pitch?: string;
  recaptchaToken?: string;
}) => {
  const params = new URLSearchParams();
  params.set('listing_id', String(payload.listingId));
  params.set('inquirer_email', payload.inquirerEmail);
  if (payload.inquirerName) params.set('inquirer_name', payload.inquirerName);
  if (payload.pitch)        params.set('pitch', payload.pitch);
  if (payload.recaptchaToken) params.set('recaptcha_token', payload.recaptchaToken);
  const { data } = await phpApi.post('/api/public/editorial-inquiry/', params.toString(), {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
  return data;
};

export const verifyPublicReview = async (token: string) => {
  const { data } = await phpApi.get(`/api/public/review/verify/${encodeURIComponent(token)}`);
  return data;
};

export const fetchFirstPartyReviews = async (listingId: number): Promise<FirstPartyReviewsResponse> => {
  const { data } = await phpApi.get(`/api/public/review/listing/${listingId}`);
  return data;
};

export const fetchListingById = async (id: number): Promise<Listing | null> => {
  try {
    const { data } = await api.get<Listing>(`/directory/listing/${id}`);
    return data;
  } catch {
    return null;
  }
};

export const fetchListingsByState = async (
  state: string,
  page = 1,
  limit = 20
): Promise<ListingsResponse> => {
  const { data } = await api.get<ListingsResponse>(
    `/directory/listings/state/${state}`,
    { params: { page, limit } }
  );
  return data;
};

export const fetchListingsByCity = async (
  state: string,
  city: string,
  page = 1,
  limit = 20
): Promise<ListingsResponse> => {
  const encoded = encodeURIComponent(city);
  const { data } = await api.get<ListingsResponse>(
    `/directory/listings/city/${state}/${encoded}`,
    { params: { page, limit } }
  );
  return data;
};

/**
 * Fetch ad markup from the adserver.
 *
 * Browsers forbid JavaScript from manually setting the Referer header.
 * Instead, we pass the current page URL as the ?ref= query parameter so
 * the adserver can parse the state/region from the URL path.
 *
 * @param pageUrl  The full URL of the page displaying the ad (e.g. https://topratedrehabs.com/rehabs/florida/)
 * @param params   Ad format counts, e.g. { sidebar: 1 } or { billboard: 2 }
 */
export const fetchAds = async (
  pageUrl: string,
  params: Record<string, number>
): Promise<AdResponse> => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => searchParams.set(k, String(v)));
  // Pass pageUrl as ?ref= — the adserver uses this as the referer source
  searchParams.set('ref', pageUrl);
  const { data } = await api.get<AdResponse>(`/listing/?${searchParams.toString()}`);
  return data;
};

export const fetchVipRegions = async () => {
  const { data } = await api.get('/vip/regions');
  return data;
};

export const fetchActiveVipRegions = async () => {
  const { data } = await api.get('/vip/regions/active');
  return data;
};

export const geocodeIp = async (ip: string) => {
  const { data } = await api.get(`/geocode/ipv4/${ip}/`);
  return data;
};

// --- Claims ---
export const submitClaim = async (claim: {
  listingId: number; name: string; email: string; phone: string;
  businessRole: string; verificationNotes: string;
}): Promise<ClaimRequest> => {
  const { data } = await api.post('/claims/', claim);
  return data;
};

export const fetchClaims = async (status?: string): Promise<ClaimRequest[]> => {
  const params: any = {};
  if (status) params.status = status;
  const { data } = await api.get('/claims/', { params, headers: authHeader() });
  return data;
};

export const approveClaim = async (claimId: string, adminNotes?: string) => {
  const { data } = await api.put(`/claims/${claimId}/approve`, { admin_notes: adminNotes }, { headers: authHeader() });
  return data;
};

export const rejectClaim = async (claimId: string, adminNotes?: string) => {
  const { data } = await api.put(`/claims/${claimId}/reject`, { admin_notes: adminNotes }, { headers: authHeader() });
  return data;
};

// --- Analytics ---
export const trackEvent = async (event: { listingId: number; eventType: string; source?: string }) => {
  const { data } = await api.post('/analytics/event', event);
  return data;
};

// --- Leads ---
export const submitLead = async (lead: {
  listingId: number; name: string; email: string; phone: string; message: string; source?: string;
}): Promise<Lead> => {
  const { data } = await api.post('/leads/', lead);
  return data;
};

export const updateLeadStatus = async (leadId: string, status: string) => {
  const { data } = await api.put(`/leads/${leadId}/status`, { status }, { headers: authHeader() });
  return data;
};

// --- Compare ---
export const compareListings = async (listingIds: number[]): Promise<Listing[]> => {
  const { data } = await api.post('/directory/compare', { listingIds });
  return data.listings || data;
};

// --- Search ---
export const searchListings = async (query: string, page = 1, limit = 20): Promise<ListingsResponse> => {
  const { data } = await api.get('/directory/search', { params: { q: query, page, limit } });
  return data;
};

export const fetchListingsByZip = async (zip: string, page = 1, limit = 20): Promise<ListingsResponse> => {
  const { data } = await api.get(`/directory/listings/zip/${zip}`, { params: { page, limit } });
  return data;
};

export const fetchListingsByFilter = async (filters: Record<string, string>, page = 1, limit = 20): Promise<ListingsResponse> => {
  const { data } = await api.get('/directory/listings/filter', { params: { ...filters, page, limit } });
  return data;
};

// --- AI Assistant ---
export const queryAssistant = async (query: string): Promise<AssistantResponse> => {
  const { data } = await api.post('/assistant/query', { query });
  return data;
};

// Auth header helper (uses a token from env or localStorage)
function authHeader(): Record<string, string> {
  const token = process.env.REACT_APP_API_TOKEN || localStorage.getItem('apiToken') || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
}
